import { RoutingConstants } from '~/apps/smartsend/lib/consts/RoutingConstants';
import AccountLinking from '../account-linking/account-linking';
import KnowYourCustomerAccountLinking from '../account-linking/know-your-customer';
import ApiClients from '../api-clients/api-clients';
import Settings from '../settings/settings';
import Addresses from '../addresses/addresses';
import Billing from '../billing/billing';
import StoredCards from '../stored-cards/stored-cards';
import MyProducts from '../my-products/my-products';
import MyPlan from '../my-plan/my-plan';
import { SettingsCache } from '~/lib/consts/settings-cache';
import { useHasSubscription } from '~/apps/subscriptions/use-has-subscription';
import { useMemo } from 'react';
import useSubscriptionsEnabled from '~/hooks/use-subscriptions-enabled';
import { hasSeenSubscriptionInOnboarding } from '~/apps/subscriptions/subscription-seen-in-onboarding';

export const childrenOfAccount = [
  {
    path: RoutingConstants.AccountLinking,
    element: <AccountLinking />
  },
  {
    path: RoutingConstants.MyPlan,
    element: <MyPlan />
  },
  {
    path: RoutingConstants.VerifyYourIdentity,
    element: <KnowYourCustomerAccountLinking />
  },
  {
    path: RoutingConstants.Settings,
    element: <Settings />
  },
  {
    path: RoutingConstants.SavedAddresses,
    element: <Addresses />
  },
  {
    path: RoutingConstants.MyProducts,
    element: <MyProducts />
  },
  {
    path: RoutingConstants.Billing,
    element: <Billing />
  },
  {
    path: RoutingConstants.StoredCards,
    element: <StoredCards />
  },
  {
    path: RoutingConstants.ApiClients,
    element: <ApiClients />
  }
];

export const routesOfAccount = childrenOfAccount.map((r) => r.path);

const sanitisedRoutesOfAccount = (
  subscriptionsEnabled: boolean,
  hasSubscription: boolean,
  seenInOnboarding: boolean
) => {
  const ouput = routesOfAccount.filter((r) => {
    switch (r) {
      case RoutingConstants.MyPlan: {
        return subscriptionsEnabled || hasSubscription || seenInOnboarding;
      }

      case RoutingConstants.VerifyYourIdentity:
        return SettingsCache.settings.Features.includes('KnowYourCustomer');

      case RoutingConstants.StoredCards:
        return SettingsCache.settings.Features.includes('StoredCards');

      case RoutingConstants.MyProducts:
        return SettingsCache.settings.Features.includes('Products');

      case RoutingConstants.ApiClients:
        return SettingsCache.settings.Features.includes('ApiClients');

      default:
        return true;
    }
  });

  // If hasSubscription is false, return as the first in the array
  if (subscriptionsEnabled && !hasSubscription) {
    const index = ouput.indexOf(RoutingConstants.MyPlan);
    if (index > -1) {
      ouput.splice(index, 1);
      ouput.unshift(RoutingConstants.MyPlan);
    }
  }

  return ouput;
};

export const useSanitisedRoutesOfAccount = () => {
  const subscriptionsEnabled = useSubscriptionsEnabled();
  const hasSubscription = useHasSubscription();
  // Read every render rather than once: the sidebar stays mounted while onboarding sets this flag.
  const seenInOnboarding = hasSeenSubscriptionInOnboarding();
  const sanitisedRoutesOfAccountMemo = useMemo(
    () => sanitisedRoutesOfAccount(subscriptionsEnabled, hasSubscription, seenInOnboarding),
    [subscriptionsEnabled, hasSubscription, seenInOnboarding]
  );
  return sanitisedRoutesOfAccountMemo;
};
